import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { ResourceUtilizationGrantType, ResourceUtilizationOffer, ResourceUtilizationServiceProviderUnit } from '../../api-service/clients';
import { DatePicker, DatePickerValue } from '../../components/date-picker';
import {
  ResourceUtilizationChip,
  ResourceUtilizationFilters,
  ResourceUtilizationOverview,
  ResourceUtilizationRow,
  ResourceUtilizationView,
  resourceUtilizationViews,
} from './resource-utilization';

const colors = {
  allocated: '#49788e',
  other: '#a9bfcb',
  over: '#f59a23',
  free: '#e3eaef',
  freeText: '#2e7d32',
  overText: '#9a5b00',
};

const statusColors = {
  over: { color: '#9a5b00', bgcolor: '#fdf2e1' },
  full: { color: 'rgba(29, 39, 51, 0.66)', bgcolor: '#e9edf1' },
  free: { color: '#2e7d32', bgcolor: '#eaf4ea' },
};

const numberCell = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export interface ResourceUtilizationComponentProps {
  date: Date;
  isLoading: boolean;
  serviceProviderUnits: ResourceUtilizationServiceProviderUnit[];
  offers: ResourceUtilizationOffer[];
  grantTypes: ResourceUtilizationGrantType[];
  filters: ResourceUtilizationFilters;
  chips: ResourceUtilizationChip[];
  view: ResourceUtilizationView;
  overview: ResourceUtilizationOverview;
  onChangeDate: (value: DatePickerValue) => void;
  onSelectServiceProviderUnit: (serviceProviderUnitId: string | undefined) => void;
  onSelectOffer: (offerId: string | undefined) => void;
  onSelectGrantType: (grantTypeId: string | undefined) => void;
  onRemoveFilter: (filter: keyof ResourceUtilizationFilters) => void;
  onResetFilters: () => void;
  onSelectView: (view: ResourceUtilizationView) => void;
  onDrillDown: (key: string) => void;
  onOpenActivity: (activityId: string) => void;
}

interface FilterSelectProps {
  label: string;
  allLabel: string;
  value: string | undefined;
  options: Array<{ id: string; name: string }>;
  onSelect: (value: string | undefined) => void;
}

function FilterSelect({ label, allLabel, value, options, onSelect }: FilterSelectProps) {
  return (
    <TextField select size="small" variant="filled" label={label} value={value ?? ''} onChange={event => onSelect(event.target.value || undefined)} sx={{ minWidth: 220 }}>
      <MenuItem value="">{allLabel}</MenuItem>
      {options.map(option => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function KpiTile(props: { label: string; value: string; description: string; color?: string }) {
  return (
    <Paper sx={{ p: 2, flex: '1 1 0', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
        {props.label}
      </Typography>
      <Typography sx={{ fontSize: '1.85rem', fontWeight: 500, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums', color: props.color ?? '#184c61' }}>{props.value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {props.description}
      </Typography>
    </Paper>
  );
}

function UtilizationBar({ row }: { row: ResourceUtilizationRow }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ flex: '1 1 auto', display: 'flex', height: 10, borderRadius: '2px', overflow: 'hidden' }}>
        <Box sx={{ width: `${row.bar.allocatedPercent}%`, bgcolor: colors.allocated }} />
        <Box sx={{ width: `${row.bar.otherPercent}%`, bgcolor: colors.other }} />
        <Box sx={{ width: `${row.bar.overPercent}%`, bgcolor: colors.over }} />
        <Box sx={{ width: `${row.bar.freePercent}%`, bgcolor: colors.free }} />
      </Box>
      <Box component="span" sx={{ width: 44, ...numberCell }}>
        {row.percentage}
      </Box>
    </Box>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="span" sx={{ width: 12, height: 10, borderRadius: '2px', bgcolor: color }} />
      {label}
    </Box>
  );
}

export function ResourceUtilizationComponent(props: ResourceUtilizationComponentProps) {
  const { filters, overview, view } = props;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const selectedGrantType = props.grantTypes.find(x => x.id === filters.grantTypeId);
  const viewLabel = resourceUtilizationViews.find(x => x.view === view)?.label ?? '';

  function toggleRow(key: string) {
    setExpandedRows({ ...expandedRows, [key]: !expandedRows[key] });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}>
            Udfører · Ressourcer
          </Typography>
          <Typography variant="h1" sx={{ fontSize: '1.35rem' }}>
            Ressourceforbrug
          </Typography>
        </Box>
        <Button variant="outlined" color="inherit" disabled title="Eksporten er endnu ikke tilgængelig">
          Eksportér til Excel
        </Button>
      </Box>

      <Paper sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <Box sx={{ width: 180 }}>
            <DatePicker label="Opgjort pr." value={props.date} onChange={props.onChangeDate} />
          </Box>
          <FilterSelect
            label="Udførerenhed"
            allLabel="Alle udførerenheder"
            value={filters.serviceProviderUnitId}
            options={props.serviceProviderUnits}
            onSelect={props.onSelectServiceProviderUnit}
          />
          <FilterSelect label="Tilbud" allLabel="Alle tilbud" value={filters.offerId} options={props.offers} onSelect={props.onSelectOffer} />
          <FilterSelect label="Bevillingstype" allLabel="Alle bevillingstyper" value={filters.grantTypeId} options={props.grantTypes} onSelect={props.onSelectGrantType} />
          <Button variant="text" sx={{ ml: 'auto' }} onClick={props.onResetFilters}>
            Nulstil filtre
          </Button>
        </Box>
        {props.chips.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Viser kun:
            </Typography>
            {props.chips.map(chip => (
              <Chip key={chip.filter} label={chip.label} onDelete={() => props.onRemoveFilter(chip.filter)} sx={{ fontWeight: 600 }} />
            ))}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <KpiTile label="ATA-tid pr. uge" value={overview.kpis.capacity} description={`Tid hos borger · ${overview.kpis.employeeCount} medarbejdere`} />
        <KpiTile
          label={overview.kpis.allocatedLabel}
          value={overview.kpis.allocated}
          description={`${overview.kpis.allocatedPercentage} af ATA-tiden · ${overview.kpis.activityCount} aktiviteter`}
        />
        <KpiTile label="Ledig ATA-tid" value={overview.kpis.free} color={colors.freeText} description={`fordelt på ${overview.kpis.freeEmployeeCount} medarbejdere`} />
        <KpiTile label="Over ATA-tid" value={overview.kpis.over} color={colors.overText} description={`${overview.kpis.overEmployeeCount} medarbejdere overbooket`} />
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, bgcolor: '#f4f4f4', borderBottom: '1px solid rgba(0, 0, 0, 0.15)' }}>
          <Typography sx={{ fontWeight: 500 }}>Fordeling</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            aria-label="Vis fordeling efter"
            onChange={(_, value: ResourceUtilizationView | null) => value && props.onSelectView(value)}
          >
            {resourceUtilizationViews.map(option => (
              <ToggleButton key={option.view} value={option.view} sx={{ textTransform: 'none', fontWeight: 600 }}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{viewLabel}</TableCell>
                <TableCell sx={numberCell}>Aktiviteter</TableCell>
                <TableCell sx={numberCell}>{selectedGrantType ? `Allokeret (${selectedGrantType.shortName})` : 'Allokeret'}</TableCell>
                <TableCell sx={numberCell}>ATA</TableCell>
                <TableCell sx={{ width: '32%' }}>{overview.showsCapacity ? 'Udnyttelse af ATA-tid' : 'Andel af allokerede timer'}</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!props.isLoading && overview.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    Ingen aktiviteter matcher de valgte filtre.{' '}
                    <Button variant="text" onClick={props.onResetFilters}>
                      Nulstil filtre
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {!props.isLoading &&
                overview.rows.map(row => {
                  const isExpanded = view === 'employee' && !!expandedRows[row.key];
                  return (
                    <React.Fragment key={row.key}>
                      <TableRow hover>
                        <TableCell>
                          {view === 'employee' ? (
                            <Link
                              component="button"
                              underline="hover"
                              sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                              aria-expanded={isExpanded}
                              aria-label={`${isExpanded ? 'Skjul' : 'Vis'} aktiviteter for ${row.name}`}
                              onClick={() => toggleRow(row.key)}
                            >
                              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size="xs" />
                              {row.name}
                            </Link>
                          ) : row.canDrillDown ? (
                            <Link
                              component="button"
                              underline="hover"
                              sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                              onClick={() => props.onDrillDown(row.key)}
                            >
                              {row.name}
                              <FontAwesomeIcon icon={faChevronRight} size="xs" />
                            </Link>
                          ) : (
                            <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                          )}
                          <Typography variant="caption" component="div" color="text.secondary">
                            {row.description}
                          </Typography>
                        </TableCell>
                        <TableCell sx={numberCell}>{row.activityCount}</TableCell>
                        <TableCell sx={numberCell}>
                          <div>{row.allocated}</div>
                          {row.otherAllocated && (
                            <Typography variant="caption" color="text.secondary">
                              + {row.otherAllocated} øvrige
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={numberCell}>{row.capacity}</TableCell>
                        <TableCell>
                          <UtilizationBar row={row} />
                        </TableCell>
                        <TableCell>
                          {row.status ? (
                            <Chip size="small" label={row.status.text} sx={{ fontWeight: 600, ...statusColors[row.status.kind] }} />
                          ) : (
                            <Typography color="text.secondary">–</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ pl: 5, pb: 2, bgcolor: '#f7f9fb' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Aktivitet</TableCell>
                                  <TableCell>Bevillingstype</TableCell>
                                  <TableCell>Tilbud</TableCell>
                                  <TableCell>Start</TableCell>
                                  <TableCell sx={numberCell}>Timer pr. uge</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {row.activities.map(activity => (
                                  <TableRow key={activity.activityId}>
                                    <TableCell>
                                      <Link component="button" underline="hover" onClick={() => props.onOpenActivity(activity.activityId)}>
                                        {`Aktivitet ${activity.activityIdentifier}`}
                                      </Link>
                                      {activity.activityTitle && (
                                        <Typography variant="caption" component="div" color="text.secondary">
                                          {activity.activityTitle}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>{activity.grantTypeName}</TableCell>
                                    <TableCell>{activity.offerName}</TableCell>
                                    <TableCell>{activity.startDate}</TableCell>
                                    <TableCell sx={numberCell}>{activity.allocated}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                              <TableFooter>
                                <TableRow>
                                  <TableCell colSpan={4} sx={{ fontWeight: 600 }}>{`I alt · ${row.activities.length} aktiviteter`}</TableCell>
                                  <TableCell sx={{ ...numberCell, fontWeight: 600 }}>{row.activitiesTotal}</TableCell>
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>I alt</TableCell>
                <TableCell sx={{ ...numberCell, fontWeight: 600 }}>{overview.total.activityCount}</TableCell>
                <TableCell sx={{ ...numberCell, fontWeight: 600 }}>{overview.total.allocated}</TableCell>
                <TableCell sx={{ ...numberCell, fontWeight: 600 }}>{overview.total.capacity}</TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2.5,
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderTop: '1px solid #e3e8ed',
            color: 'text.secondary',
            fontSize: '0.8rem',
          }}
        >
          <LegendItem color={colors.allocated} label={selectedGrantType ? 'Valgt bevillingstype' : 'Allokeret'} />
          {selectedGrantType && overview.showsCapacity && <LegendItem color={colors.other} label="Øvrige bevillingstyper" />}
          <LegendItem color={colors.free} label="Ledig ATA-tid" />
          <LegendItem color={colors.over} label="Over ATA-tid" />
          <Box component="span" sx={{ ml: 'auto' }}>
            {!overview.showsCapacity
              ? 'ATA-tid hører til medarbejderen – ikke til bevillingstypen. Klik på en række for at se medarbejderne.'
              : view === 'employee'
              ? 'ATA = tid hos borger pr. uge. Klik på en medarbejder for at se de aktiviteter, timerne er summeret fra.'
              : 'Klik på en række for at se den nærmere.'}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
